/**
 * Standard API error handler that provides consistent error handling across the application
 * @param error - The caught error object or unknown error value
 * @param action - Description of the action that failed (for error messages)
 * @returns Object containing null data and a user-friendly error message
 */
export function handleApiError(error: unknown, action: string) {
  // Error logging removed for production

  return {
    data: null,
    error: error instanceof Error ? error.message : `An unknown error occurred while ${action}`,
  }
}

/**
 * Wraps an async function with standard error handling
 * @param fn - The async function to wrap
 * @param actionDescription - Description of the action for error messaging
 * @returns A wrapped function with consistent error handling
 */
export function withErrorHandling<T, Args extends any[]>(
  fn: (...args: Args) => Promise<T>,
  actionDescription: string
) {
  return async (...args: Args): Promise<{ data: T | null; error: string | null }> => {
    try {
      const result = await fn(...args)
      return { data: result, error: null }
    } catch (error) {
      return handleApiError(error, actionDescription)
    }
  }
}
